import { makeStyles } from '@material-ui/styles'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Button from 'core/elements/button'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import React, { useCallback, useMemo, useState } from 'react'
import useReactRouter from 'use-react-router'
import { VMHABannerState, VMHAHostStatus } from '../vmha/types'
import { formatVMHABannerMessage } from '../vmha/vmhaHelpers'
import VMHADetailsDrawer from './VMHADetailsDrawer'

interface VMHABannerProps {
  hostId: string
  vmhaStatus?: VMHAHostStatus
  isHostOnline?: boolean
}

const useStyles = makeStyles<Theme, { state: VMHABannerState }>((theme) => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderRadius: '4px',
    marginBottom: '24px',
    backgroundColor: ({ state }) => {
      switch (state) {
        case VMHABannerState.InProgress:
          return theme.palette.blue[150] // Light blue background
        case VMHABannerState.Completed:
          return theme.palette.green[150] // Light green background
        case VMHABannerState.Warning:
          return theme.palette.yellow[150] // Light yellow background
        case VMHABannerState.Error:
          return theme.palette.red[150] // Light red background
        default:
          return theme.palette.grey[150] // Light grey background
      }
    },
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  content: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(2),
    flex: 1,
  },
  icon: {
    width: '24px',
    height: '24px',
    color: ({ state }) => {
      switch (state) {
        case VMHABannerState.InProgress:
          return theme.palette.blue[500]
        case VMHABannerState.Completed:
          return theme.palette.green[500]
        case VMHABannerState.Warning:
          return theme.palette.yellow[500]
        case VMHABannerState.Error:
          return theme.palette.red[500]
        default:
          return theme.palette.grey[500]
      }
    },
  },
  message: {
    display: 'grid',
    gridAutoFlow: 'row',
    gap: theme.spacing(1),
  },
  details: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
  },
  detailsButton: {
    marginLeft: theme.spacing(2),
  },
  additionalInfo: {
    width: '100%',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
}))

const VMHABanner: React.FC<VMHABannerProps> = ({ hostId, vmhaStatus, isHostOnline = false }) => {
  const { location } = useReactRouter()
  const searchParams = new URLSearchParams(location.search)
  const showVMHADetails = useMemo(() => searchParams.get('showVMHADetails') === 'true', [
    searchParams,
  ])

  // Call useStyles hook before any conditional returns to comply with Rules of Hooks
  const classes = useStyles({ state: vmhaStatus?.state || VMHABannerState.Completed })
  const [drawerOpen, setDrawerOpen] = useState(showVMHADetails)

  const handleOpenDrawer = useCallback(() => {
    setDrawerOpen(true)
  }, [])

  const handleCloseDrawer = useCallback(() => {
    // Remove showVMHADetails parameter from URL when closing
    if (showVMHADetails) {
      window.history.replaceState({}, '', location.pathname)
    }

    setDrawerOpen(false)
  }, [showVMHADetails, location])

  // We dont need to show the banner if the host is online and the VMHA event is completed
  const isVMHAEventCompletedWithHostOnline =
    isHostOnline && vmhaStatus?.state === VMHABannerState.Completed

  // Check if vmhaStatus is valid AFTER all hooks are called
  if (!vmhaStatus || vmhaStatus.hostId !== hostId || isVMHAEventCompletedWithHostOnline) {
    return null
  }

  const iconName = {
    [VMHABannerState.InProgress]: 'circle-info',
    [VMHABannerState.Completed]: 'circle-check',
    [VMHABannerState.Warning]: 'triangle-exclamation',
    [VMHABannerState.Error]: 'circle-exclamation',
  }[vmhaStatus.state]

  const { mainMessage, details, additionalInfo } = formatVMHABannerMessage(
    vmhaStatus.state,
    vmhaStatus.event.totalVms,
    vmhaStatus.eventOccurrenceTime,
    vmhaStatus.tasks,
  )

  return (
    <>
      <div className={classes.banner} data-testid="vmha-event-banner">
        <div className={classes.content}>
          <FontAwesomeIcon className={classes.icon} solid>
            {iconName}
          </FontAwesomeIcon>
          <div className={classes.message}>
            <Text variant="body1">
              <b>{mainMessage}</b>
            </Text>
            <div className={classes.details}>{details}</div>
            {additionalInfo && (
              <Text className={classes.additionalInfo} variant="body2">
                {additionalInfo}
              </Text>
            )}
          </div>
        </div>
        <Button
          className={classes.detailsButton}
          variant="secondary"
          onClick={handleOpenDrawer}
          data-testid="vmha-details-button"
        >
          See Details
        </Button>
      </div>
      <VMHADetailsDrawer open={drawerOpen} onClose={handleCloseDrawer} vmhaStatus={vmhaStatus} />
    </>
  )
}

export default VMHABanner
